import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { FormHelperText } from '@material-ui/core';
import moment from 'moment';
import ControllerSelect from 'components/ControlledSelect/ControlledSelect';
import { useReportingMode } from 'modules/OrderReporting/useReportingMode';
import {
  isInternalClientName,
  isOrderRequiredOn,
} from 'modules/OrderReporting/utils';
import { fetchEligibleTripOrders } from '../../api';
import { HELPER_TEXT_MARGIN } from '../../constants';
import {
  BusinessTripFormValues,
  EligibleTripOrder,
  TripOrderOption,
} from '../../types';
import { toTripDate, toTripOrderOption } from '../../utils';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export interface TripClient {
  value?: number | string | null;
  name?: string | null;
}

interface TripOrderSelectProps {
  control: Control<BusinessTripFormValues>;
  from?: Date | string | null;
  to?: Date | string | null;
  accountId?: number;
  // Only orders for this client are offered once a client is chosen.
  client?: TripClient | null;
  defaultValue?: TripOrderOption | null;
  setValue: (
    name: 'order',
    value: TripOrderOption | null,
    config?: { shouldValidate?: boolean },
  ) => void;
}

// Eligible orders carry the client name; the id is used when the backend
// sends it.
export const isOrderForClient = (
  order: EligibleTripOrder,
  client?: TripClient | null,
) => {
  if (!client || (client.value == null && !client.name)) {
    return true;
  }
  if (order.clientId != null && client.value != null) {
    return order.clientId === Number(client.value);
  }
  return order.clientName === client.name;
};

const TripOrderSelect = ({
  control,
  from,
  to,
  accountId,
  client,
  defaultValue,
  setValue,
}: TripOrderSelectProps) => {
  const intl = useIntl();
  const [state, setState] = useState<LoadState>('idle');
  const [eligible, setEligible] = useState<EligibleTripOrder[]>([]);
  const { reportingMode } = useReportingMode();
  const fromDate = toTripDate(from);
  const toDate = toTripDate(to);
  const selected = useWatch<TripOrderOption | null | undefined>({
    control,
    name: 'order',
    defaultValue,
  });
  const clientValue = client?.value;
  const clientName = client?.name;
  // Trips for an internal client (JIT Team, JIT Other) need no work order.
  const internal = isInternalClientName(clientName);
  // In enforcing mode a trip starting on or after the effective date must
  // name its work order.
  const required =
    !internal &&
    Boolean(fromDate) &&
    isOrderRequiredOn(reportingMode, fromDate);
  const options = useMemo(
    () =>
      internal
        ? []
        : eligible
            .filter((order) =>
              isOrderForClient(order, {
                value: clientValue,
                name: clientName,
              }),
            )
            .map((order) => toTripOrderOption(order, intl)),
    [clientName, clientValue, eligible, intl, internal],
  );

  useEffect(() => {
    if (!fromDate || !toDate) {
      setState('idle');
      setEligible([]);
      return;
    }
    let active = true;
    setState('loading');
    fetchEligibleTripOrders({ accountId, from: fromDate, to: toDate }).then(
      (result) => {
        if (!active) {
          return;
        }
        if (result.ok && Array.isArray(result.data)) {
          setEligible(result.data);
          setState('ready');
        } else {
          setEligible([]);
          setState('error');
        }
      },
    );
    return () => {
      active = false;
    };
  }, [accountId, fromDate, toDate]);

  // Keep the selection in step with the eligible orders for the dates:
  // drop an order that no longer covers them and complete a prefilled one.
  useEffect(() => {
    if (internal) {
      if (selected) {
        setValue('order', null);
      }
      return;
    }
    if (state !== 'ready') {
      return;
    }
    if (!selected) {
      // Nothing to choose when exactly one order covers the trip dates.
      if (options.length === 1) {
        setValue('order', options[0]);
      }
      return;
    }
    const match = options.find(({ value }) => value === selected.value);
    if (!match) {
      setValue('order', null);
    } else if (match.name !== selected.name) {
      setValue('order', match);
    }
  }, [internal, options, selected, setValue, state]);

  // Re-validate when the requirement changes so the step can (or cannot)
  // continue without an order.
  const previousRequired = useRef(required);
  useEffect(() => {
    if (previousRequired.current !== required) {
      previousRequired.current = required;
      setValue('order', selected || null, { shouldValidate: true });
    }
  }, [required, selected, setValue]);

  const message = (() => {
    if (internal) {
      return 'BUSINESS_TRIP.WORK_ORDER.INTERNAL';
    }
    if (state === 'idle') {
      return 'BUSINESS_TRIP.WORK_ORDER.CHOOSE_DATES';
    }
    if (state === 'loading') {
      return 'BUSINESS_TRIP.WORK_ORDER.LOADING';
    }
    if (state === 'error') {
      return 'BUSINESS_TRIP.WORK_ORDER.ERROR';
    }
    if (!options.length) {
      return eligible.length
        ? 'BUSINESS_TRIP.WORK_ORDER.NONE_FOR_CLIENT'
        : 'BUSINESS_TRIP.WORK_ORDER.NONE';
    }
    return required && !selected ? 'BUSINESS_TRIP.WORK_ORDER.REQUIRED' : '';
  })();
  const isError =
    state === 'error' ||
    (!internal && state === 'ready' && !options.length) ||
    message === 'BUSINESS_TRIP.WORK_ORDER.REQUIRED';

  return (
    <>
      <ControllerSelect
        id="trip-work-order"
        label="BUSINESS_TRIP.FORM.WORK_ORDER"
        name="order"
        control={control}
        options={options}
        defaultValue={defaultValue || undefined}
        isPadding={false}
        isClearable
        helperText
        required={required}
        isDisabled={internal || state !== 'ready' || !options.length}
        noOptionMessage={intl.formatMessage({
          id: 'BUSINESS_TRIP.WORK_ORDER.NONE',
        })}
      />
      {message && (
        <FormHelperText error={isError} style={{ margin: HELPER_TEXT_MARGIN }}>
          {intl.formatMessage(
            { id: message },
            {
              date: reportingMode?.effectiveDate
                ? moment(reportingMode.effectiveDate).format('DD/MM/YYYY')
                : '',
            },
          )}
        </FormHelperText>
      )}
    </>
  );
};

export default TripOrderSelect;
